import { useEffect, useState } from "react";
import { getIdea, putIdea } from "../data/ideaStore";
import type { ModelInfo } from "../data/modelStore";
import {
  type Idea,
  type ScanResult,
  type Settings,
  scanResultOf,
} from "../data/types";
import { activeEngineLabel } from "../engine/active";
import { runScan } from "../engine/scan";

interface Props {
  ideaId: string;
  settings: Settings;
  installedModel: ModelInfo | null;
  onBack: () => void;
  onIdeaSaved: () => void;
}

type State =
  | { kind: "loading" }
  | { kind: "noIdea" }
  | { kind: "idle"; idea: Idea }
  | { kind: "running"; idea: Idea }
  | { kind: "done"; idea: Idea; result: ScanResult }
  | { kind: "error"; idea: Idea; message: string };

export function ScanScreen(props: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const idea = await getIdea(props.ideaId);
      if (cancelled) return;
      if (!idea) return setState({ kind: "noIdea" });
      const existing = scanResultOf(idea);
      setState(existing ? { kind: "done", idea, result: existing } : { kind: "idle", idea });
    })();
    return () => {
      cancelled = true;
    };
  }, [props.ideaId]);

  const handleRun = async () => {
    if (state.kind !== "idle" && state.kind !== "done" && state.kind !== "error") return;
    const baseIdea = state.idea;
    setState({ kind: "running", idea: baseIdea });
    const outcome = await runScan(baseIdea, props.settings, props.installedModel);
    if (outcome.kind === "error") {
      setState({ kind: "error", idea: baseIdea, message: outcome.message });
      return;
    }
    const updated: Idea = {
      ...baseIdea,
      scanResult: outcome.result,
      state: baseIdea.state === "Capturing" ? "Scanning" : baseIdea.state,
      updatedAtMs: Date.now(),
    };
    await putIdea(updated);
    props.onIdeaSaved();
    setState({ kind: "done", idea: updated, result: outcome.result });
  };

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-black/5 bg-bg/90 px-4 py-3 backdrop-blur">
        <button className="btn-ghost" onClick={props.onBack}>
          Back
        </button>
        <h1 className="text-lg font-semibold tracking-tight">Scan</h1>
        <span className="w-16" />
      </header>

      <main className="flex flex-1 flex-col gap-4 px-4 py-4">
        {state.kind === "loading" && <div className="text-sm text-ink-muted">Loading…</div>}
        {state.kind === "noIdea" && (
          <div className="text-sm text-rose-700">That idea is gone.</div>
        )}

        {(state.kind === "idle" ||
          state.kind === "running" ||
          state.kind === "done" ||
          state.kind === "error") && (
          <>
            <section className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">{state.idea.title}</h2>
              <div className="text-xs text-ink-muted">
                Engine: {activeEngineLabel(props.settings, props.installedModel)}
              </div>
            </section>

            <p className="text-sm text-ink-muted">
              The LLM brainstorms 6–10 real products related to your idea — direct competitors,
              adjacent alternatives, analogues. Used downstream to ground personas.{" "}
              <em>v2 plan</em>: replace with a Product Hunt API call.
            </p>

            {state.kind === "idle" && (
              <button className="btn-primary self-start" onClick={handleRun}>
                Run scan
              </button>
            )}
            {state.kind === "running" && (
              <div className="card flex flex-col gap-2 p-4">
                <div className="text-sm font-medium">Scanning…</div>
                <div className="text-xs text-ink-muted">
                  First on-device run may take 5–15 s while Gemma warms up.
                </div>
              </div>
            )}
            {state.kind === "error" && (
              <div className="rounded-xl bg-rose-50 p-4 text-sm text-rose-900 ring-1 ring-rose-200">
                <div className="font-semibold">Scan failed</div>
                <p className="mt-1 whitespace-pre-wrap">{state.message}</p>
                <button className="btn-ghost mt-3" onClick={handleRun}>
                  Retry
                </button>
              </div>
            )}

            {state.kind === "done" && (
              <section className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">
                    {state.result.products.length} products found
                  </h3>
                  <button className="btn-ghost" onClick={handleRun}>
                    Re-run
                  </button>
                </div>
                <ul className="flex flex-col gap-2">
                  {state.result.products.map((p) => (
                    <li key={p.name} className="card p-3">
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-sm">{p.tagline}</div>
                      <div className="mt-1 text-xs text-ink-muted">{p.rationale}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </>
  );
}
